import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { appReducer } from '@app-root-store';
import { withLatestFrom, map, switchMap, concatMap, catchError, delay, tap, take } from 'rxjs/operators';
import { UploadService, DocumentService } from '@app-root-services';
import * as uploadActions from '../actions/upload.actions';
import * as uploadSelectors from '../selectors/upload.selector';
import * as documentSelectors from '../selectors/document.selector';
import * as profileSelectors from '../selectors/profile.selector';
import * as documentActions from '../actions/document.actions';
import * as routerSelectors from '../selectors/router.selector';
import * as errorActions from '../actions/error.actions';
import { IUploadedFile, IError } from '@app-root-interfaces';
import { EUploadState } from '@app-root-enums';
import { from, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { IProfile } from '@scf-monorepo/interfaces';
import { SiToastNotificationService } from '@siemens/element-ng/toast-notification';

@Injectable()
export class UploadEffects {
    private actions$ = inject(Actions);
    private store$ = inject<Store<appReducer.AppState>>(Store);
    private uploadService = inject(UploadService);
    private documentService = inject(DocumentService);
    private translate = inject(TranslateService);
    private readonly snackbar = inject(SiToastNotificationService);

    addFiles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(uploadActions.addFiles),
            withLatestFrom(
                this.store$.pipe(select(documentSelectors.selectDocumentCurrentFolder)),
                this.store$.pipe(select(routerSelectors.selectUrl)),
                this.store$.pipe(select(documentSelectors.selectDocumentDrive)),
                this.store$.pipe(select(documentSelectors.selectDocumentBasicFolder))
            ),
            map(([{ files }, folder, _url, drive, _basicFolder]) =>
                files.map((file) => {
                    const parentIsDrive = folder.id === drive.id;
                    return {
                        ...file,
                        drive: parentIsDrive ? folder : drive,
                        parentDrive: parentIsDrive ? folder : undefined,
                        parentFolders: parentIsDrive ? undefined : [{ id: folder.id, name: folder.name || '' }]
                    };
                })
            ),
            map((files) => uploadActions.addFilesSuccess({ files }))
        )
    );

    updateUploadedFile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(uploadActions.updateUploadedFile),
            withLatestFrom(this.store$.pipe(select(uploadSelectors.selectUploadGetActiveFile))),
            switchMap(([{ values }, original]) =>
                this.uploadService.createUpdatedFile(original, values).pipe(
                    map((data) =>
                        uploadActions.updateUploadedFileSuccess({
                            values: this.checkForCompletion(data)
                        })
                    )
                )
            )
        )
    );

    updatePreview$ = createEffect(() =>
        this.actions$.pipe(
            ofType(uploadActions.updatePreview),
            withLatestFrom(this.store$.pipe(select(uploadSelectors.selectUploadGetActiveFile))),
            map(([{ preview }, original]) => {
                const file = { ...original, preview };
                return uploadActions.updatePreviewSuccess({ values: file });
            })
        )
    );

    setFilesToUploading$ = createEffect(() =>
        this.actions$.pipe(
            ofType(uploadActions.startUploadingReadyFiles),
            withLatestFrom(this.store$.pipe(select(uploadSelectors.selectUploadGetReadyFiles))),
            map(([, files]) => {
                const updatedFiles = files.map((file) => ({
                    ...file,
                    uploadState: EUploadState.UPLOADING
                }));
                return uploadActions.updateMultipleFiles({ files: updatedFiles });
            })
        )
    );

    startUploadingReadyFiles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(uploadActions.startUploadingReadyFiles),
            withLatestFrom(this.store$.pipe(select(uploadSelectors.selectUploadGetReadyFiles))),
            map(([, files]) => files),
            withLatestFrom(this.store$.pipe(select(profileSelectors.selectProfileUserProfile))),
            map(([files, user]) =>
                files.map((file) => {
                    const userShortInfo = this.getUserShortInfo(user);
                    return {
                        ...file,
                        mimeType: file.type,
                        createdBy: userShortInfo,
                        updatedBy: userShortInfo
                    } as IUploadedFile;
                })
            ),
            switchMap((files) => from(files)),
            concatMap((file) =>
                this.documentService.createFile(file).pipe(
                    map((createdFile) => uploadActions.createSignedUrlForUpload({ file: createdFile })),
                    catchError(() => of(uploadActions.uploadingFilesFailure({ values: file })))
                )
            )
        )
    );

    createSignedUrlForUpload$ = createEffect(() =>
        this.actions$.pipe(
            ofType(uploadActions.createSignedUrlForUpload),
            withLatestFrom(this.store$.pipe(select(documentSelectors.selectDocumentDrive))),
            concatMap(([{ file }, drive]) =>
                this.uploadService.createSignedUrl(file, drive).pipe(
                    map((signedUrl) => ({
                        signedUrl,
                        file
                    })),
                    map((fileAndUrl) => uploadActions.uploadFile({ file: fileAndUrl })),
                    catchError(() => of(uploadActions.uploadingFilesFailure({ values: file })))
                )
            )
        )
    );

    uploadFile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(uploadActions.uploadFile),
            concatMap(({ file }) =>
                this.uploadService.uploadFile(file.signedUrl.signedUrl, file.file.url).pipe(
                    concatMap(() => {
                        if (file.file.preview) {
                            return of(uploadActions.createSignedUrlForPreview({ file: file.file }));
                        }
                        return of(
                            uploadActions.uploadingFilesSuccess({ values: file.file }),
                            documentActions.addFileToFolders({ file: file.file })
                        );
                    }),
                    catchError(() => of(uploadActions.uploadingFilesFailure({ values: file.file })))
                )
            )
        )
    );

    createSignedUrlForPreview$ = createEffect(() =>
        this.actions$.pipe(
            ofType(uploadActions.createSignedUrlForPreview),
            withLatestFrom(this.store$.pipe(select(documentSelectors.selectDocumentDrive))),
            concatMap(([{ file }, drive]) =>
                this.uploadService.createPreviewSignedUrl(file, drive).pipe(
                    map((data) =>
                        uploadActions.uploadPreview({
                            signedUrl: data.signedUrl,
                            file,
                            locationUrl: data.locationUrl
                        })
                    ),
                    catchError(() => of(uploadActions.uploadingFilesFailure({ values: file })))
                )
            )
        )
    );

    uploadPreview$ = createEffect(() =>
        this.actions$.pipe(
            ofType(uploadActions.uploadPreview),
            concatMap(({ file, signedUrl, locationUrl }) =>
                this.uploadService.uploadPreview(signedUrl, file.preview).pipe(
                    catchError(() =>
                        of(
                            uploadActions.uploadingFilesFailure({
                                values: {
                                    ...file,
                                    metaInfo: { ...file.metaInfo, thumbnail: file.preview.url }
                                }
                            })
                        )
                    ),
                    map(() => ({
                        ...file,
                        metaInfo: { ...file.metaInfo, thumbnail: locationUrl }
                    })),
                    concatMap((newFile) =>
                        of(
                            uploadActions.uploadingFilesSuccess({ values: newFile }),
                            documentActions.addFileToFolders({ file: newFile })
                        )
                    )
                )
            )
        )
    );

    uploadFileSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(uploadActions.uploadingFilesSuccess),
            delay(1000 * 30),
            tap(({ values }) => {
                if (values) {
                    if (values.url && values.url !== '') {
                        URL.revokeObjectURL(values.url);
                    }
                    if (values.preview && values.preview.url && values.preview.url !== '') {
                        URL.revokeObjectURL(values.preview.url);
                    }
                }
            }),
            switchMap(({ values }) => of(uploadActions.removeFileFromUploadList({ values })))
        )
    );

    addFolder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(uploadActions.addFolder),
            withLatestFrom(this.store$.pipe(select(uploadSelectors.selectUploadGetActiveFile))),
            switchMap(([{ id }, file]) =>
                this.store$.pipe(
                    select(documentSelectors.selectDocumentFolderById, { id }),
                    take(1),
                    map((folder) => ({ file, folder }))
                )
            ),
            switchMap(({ file, folder }) => {
                const duplicates = (file.parentFolders || []).filter((parent) => parent.id === folder.id);
                if (duplicates.length === 0) {
                    const newFile = { ...file };
                    newFile.parentFolders = [...(newFile.parentFolders || []), { ...folder }];
                    return of(uploadActions.addFolderSuccess({ values: newFile }));
                } else {
                    return of(uploadActions.addFolderSuccess({ values: file }));
                }
            })
        )
    );

    addDrive$ = createEffect(() =>
        this.actions$.pipe(
            ofType(uploadActions.addDrive),
            withLatestFrom(
                this.store$.pipe(select(uploadSelectors.selectUploadGetActiveFile)),
                this.store$.pipe(select(documentSelectors.selectDocumentDrive))
            ),
            switchMap(([, file, folder]) => {
                if (!file.parentDrive) {
                    const newFile = { ...file };
                    newFile.parentDrive = { ...folder };
                    return of(uploadActions.addDriveSuccess({ values: newFile }));
                } else {
                    return of(uploadActions.addDriveSuccess({ values: file }));
                }
            })
        )
    );

    removeDrive$ = createEffect(() =>
        this.actions$.pipe(
            ofType(uploadActions.removeDrive),
            withLatestFrom(this.store$.pipe(select(uploadSelectors.selectUploadGetActiveFile))),
            switchMap(([, file]) => {
                const newFile = { ...file };
                delete newFile.parentDrive;
                return of(uploadActions.removeDriveSuccess({ values: newFile }));
            })
        )
    );

    uploadInformationManagement$ = createEffect(() =>
        this.actions$.pipe(
            ofType(uploadActions.uploadInformationManagement),
            switchMap(({ files }) =>
                this.uploadService.parseManagementInformations(files).pipe(
                    map((management) => uploadActions.uploadInformationManagementSuccess({ management })),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'information-error-upload'
                        };
                        return of(errorActions.addError({ error }));
                    })
                )
            )
        )
    );

    uploadInformationLocations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(uploadActions.uploadInformationLocations),
            switchMap(({ files }) =>
                this.uploadService.parseLocationInformations(files).pipe(
                    map((locations) => uploadActions.uploadInformationLocationsSuccess({ locations })),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'information-error-upload'
                        };
                        return of(errorActions.addError({ error }));
                    })
                )
            )
        )
    );

    uploadInformationLegals$ = createEffect(() =>
        this.actions$.pipe(
            ofType(uploadActions.uploadInformationLegals),
            switchMap(({ files }) =>
                this.uploadService.parseLegalInformations(files).pipe(
                    map((legals) => uploadActions.uploadInformationLegalsSuccess({ legals })),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'information-error-upload'
                        };
                        return of(errorActions.addError({ error }));
                    })
                )
            )
        )
    );

    uploadInformationTranslations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(uploadActions.uploadInformationTranslations),
            switchMap(({ files }) =>
                this.uploadService.parseTranslationInformations(files).pipe(
                    map((translations) => uploadActions.uploadInformationTranslationsSuccess({ translations })),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'information-error-upload'
                        };
                        return of(errorActions.addError({ error }));
                    })
                )
            )
        )
    );

    submitManagementData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(uploadActions.submitManagementData),
            switchMap(({ file }) =>
                this.uploadService.submitManagementData(file).pipe(
                    map(() => uploadActions.submitManagementDataSuccess()),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'information-error-save'
                        };
                        return of(errorActions.addError({ error }), uploadActions.submitManagementDataFailure());
                    })
                )
            )
        )
    );

    submitLocationData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(uploadActions.submitLocationData),
            switchMap(({ file }) =>
                this.uploadService.submitLocationData(file).pipe(
                    map(() => uploadActions.submitLocationDataSuccess()),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'information-error-save'
                        };
                        return of(errorActions.addError({ error }), uploadActions.submitLocationDataFailure());
                    })
                )
            )
        )
    );

    submitLegalData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(uploadActions.submitLegalData),
            switchMap(({ file }) =>
                this.uploadService.submitLegalData(file).pipe(
                    map(() => uploadActions.submitLegalDataSuccess()),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'information-error-save'
                        };
                        return of(errorActions.addError({ error }), uploadActions.submitLegalDataFailure());
                    })
                )
            )
        )
    );

    submitTranslationData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(uploadActions.submitTranslationData),
            switchMap(({ file }) =>
                this.uploadService.submitTranslationData(file).pipe(
                    map(() => uploadActions.submitTranslationDataSuccess()),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'information-error-save'
                        };
                        return of(errorActions.addError({ error }), uploadActions.submitTranslationDataFailure());
                    })
                )
            )
        )
    );

    informationUploadSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    uploadActions.submitLegalDataSuccess,
                    uploadActions.submitManagementDataSuccess,
                    uploadActions.submitLocationDataSuccess,
                    uploadActions.submitTranslationDataSuccess
                ),
                tap(() => {
                    this.snackbar.queueToastNotification(
                        'success',
                        this.translate.instant('success'),
                        this.translate.instant('file-upload-success')
                    );
                })
            ),
        {
            dispatch: false
        }
    );

    // TODO schönere Lösung finden
    private checkForCompletion(file: IUploadedFile): IUploadedFile {
        if (file.uploadState === EUploadState.FINISHED || file.uploadState === EUploadState.UPLOADING) {
            return file;
        }

        let complete = true;

        if (!file.titles || file.titles.length === 0) {
            complete = false;
        } else {
            const filedTitles = file.titles.filter((title) => !!title.language && !!title.text);
            if (filedTitles.length !== file.titles.length) {
                complete = false;
            }
        }

        if (!file.languages || file.languages.length === 0) {
            complete = false;
        }

        if (!file.metaInfo || !file.metaInfo.owner || file.metaInfo.owner === '') {
            complete = false;
        }

        if (!file.metaInfo || !file.metaInfo.version || file.metaInfo.version === '') {
            complete = false;
        }

        return {
            ...file,
            uploadState: complete ? EUploadState.READY : EUploadState.EDITED
        };
    }

    private getUserShortInfo(user: IProfile) {
        return {
            id: user.id,
            orgUnit: user.department,
            company: user.organization,
            name: user.displayName
        };
    }
}
